import { FilePresent } from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Divider,
  FormControlLabel,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import dayjs from 'dayjs'
import PopupState from 'material-ui-popup-state'
import { bindDialog } from 'material-ui-popup-state/hooks'
import React, { useState } from 'react'
import { StyledButtonPrimary } from '../../styles/ButtonStyles'
import {
  StyledContainerColumnLeft,
  StyledContainerColumnRight,
  StyledContainerRowBetween,
  StyledContainerRowEnd,
  StyledContainerRowStart,
} from '../../styles/ContainerStyles'
import ScheduleDialogDescriptionBlock from '../Schedule/ScheduleDialogDescriptionBlock'
import {
  handleGetEmployeeReport,
  handleGetEmployeesReport,
  handleGetProjectReport,
} from '../../store/redux/reducers/reportsSliceAxios'
import DateFormat from '../../store/types/enums/DateFormat'
import { saveAs } from 'file-saver'
import ReportType, { ProjectReportOptions, projectsTypeArray } from '../../store/types/enums/ReportType'
import axios from 'axios'
import { useNotification } from '../../store/redux/hooks'
import { useSelector } from 'react-redux'
import { getCurrentUser, getIsLeaderAndNotManagement } from '../../store/redux/reducers/currentUserSlice'
import { createErrorMessage } from '../../store/utils'
import ReportPopupData from '../../store/types/utils/ReportPopupUtil'
import { t } from 'i18next'
import ProfileActionTile from '../../views/ProfileView/ProfileActionTile'
import { useGetLightUsersQuery } from '../../store/redux/reducers/usersSlice'
import defaultProps from '../../config/defaultProps'
import { useGetProjectsQuery } from '../../store/redux/reducers/projectsSlice'
import { getGap } from '../../styles/stylePropsFunctions'
import DateRangePickerWithPresets from '../DateRangePickerWithPresets/DateRangePickerWithPresets'

const GenerateReportPopup = ({
  selectedData,
  reportType,
  leaderId,
  profileView,
  profileActionTile,
}: {
  selectedData: ReportPopupData[]
  reportType: number
  leaderId?: string
  profileView?: boolean
  profileActionTile?: boolean
}) => {
  const theme = useTheme()

  const [selectedType, setSelectedType] = useState(ProjectReportOptions.Default)
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(dayjs().startOf('month').utc(true))
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(dayjs().endOf('month').utc(true))
  const { displayNotification } = useNotification()
  const currentUser = useSelector(getCurrentUser)
  const isLeaderAndNotManagement = useSelector(getIsLeaderAndNotManagement)
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'))
  const { data: users } = useGetLightUsersQuery(
    {
      ...defaultProps,
      id: selectedData.map(data => data.id),
    },
    { skip: selectedData.length === 0 || profileView }
  )
  const { data: projects } = useGetProjectsQuery(
    {
      ...defaultProps,
      id: selectedData.map(data => data.id),
    },
    { skip: selectedData.length === 0 || profileView }
  )
  const resetPopupState = () => {
    setStartDate(dayjs().startOf('month').utc(true))
    setEndDate(dayjs().endOf('month').utc(true))
  }

  const handleClick = (e: React.SyntheticEvent | Element | null, popupState: PopupState.InjectedProps) => {
    resetPopupState()

    if (
      isLeaderAndNotManagement &&
      selectedData.some(
        data => data.leaderIds?.every(leaderId => leaderId !== currentUser?.id) && data.id !== currentUser?.id
      )
    ) {
      displayNotification({
        message: t('page.reports.popup.notLeaderWarning'),
        type: 'error',
      })
    } else {
      popupState.open(e)
    }
  }

  return (
    <PopupState variant="popover" popupId="demo-popup-menu" disableAutoFocus={false} parentPopupState={null}>
      {popupState => (
        <React.Fragment>
          {profileView ? (
            profileActionTile ? (
              <ProfileActionTile
                Icon={FilePresent}
                title={t('page.usersProfile.buttons.report')}
                disabled={selectedData.length === 0}
                handleClick={e => handleClick(e, popupState)}
              />
            ) : (
              <Button
                startIcon={<FilePresent />}
                color="primary"
                variant="contained"
                size="small"
                sx={{ width: isMd ? '100px' : '33%', span: { marginRight: isMd ? 0 : '8px' } }}
                disabled={selectedData.length === 0}
                onClick={e => handleClick(e, popupState)}
                fullWidth={isDownSm}
              >
                {t('page.usersProfile.buttons.report')}
              </Button>
            )
          ) : (
            <Tooltip title={<Typography variant="caption">{t('page.reports.usersTileCaption')}</Typography>}>
              <span>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<FilePresent />}
                  disabled={selectedData.length === 0}
                  onClick={e => handleClick(e, popupState)}
                  fullWidth={isDownSm}
                >
                  {t('page.users.generateReport')}
                </Button>
              </span>
            </Tooltip>
          )}
          <Dialog
            {...bindDialog(popupState)}
            PaperProps={{ elevation: 2 }}
            aria-labelledby="generate-report-dialog-title"
            aria-describedby="generate-report-dialog-description"
          >
            <StyledContainerColumnLeft
              sx={{
                p: 4,
                gap: getGap(isDownSm),
              }}
            >
              <StyledContainerRowStart>
                <StyledContainerColumnLeft sx={{ width: { xs: '100%', sm: '75%', md: '75%' } }}>
                  <Typography variant="h4" color="text.highEmphasis" id="generate-report-dialog-title">
                    {t('page.reports.generateReport')}
                  </Typography>
                  {(reportType === ReportType.Employees || reportType === ReportType.Employee) && (
                    <Box marginBottom={1}>
                      {users?.content?.length && (
                        <Typography fontSize={'small'} fontStyle={'italic'} color="text.mediumEmphasis">
                          {selectedData.length > 1
                            ? t('page.users.generateForUsers', { count: selectedData.length })
                            : t('page.users.oneUser', {
                                name: users.content.map(user => user.firstName + ' ' + user.lastName),
                              })}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {ReportType.Projects === reportType && (
                    <Box marginBottom={1}>
                      {projects?.content?.length && (
                        <Typography
                          fontSize={'small'}
                          fontStyle={'italic'}
                          color="text.mediumEmphasis"
                          id="generate-report-dialog-description"
                        >
                          {selectedData.length > 1
                            ? t('page.users.generateForProject', { count: selectedData.length })
                            : t('page.users.oneProject', {
                                name: projects.content.map(project => project.acronym),
                              })}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Typography fontSize={'small'} fontStyle={'italic'} color="text.mediumEmphasis">
                    {t('page.schedule.fillOutManyPopup.overlappingInfo')}
                  </Typography>
                </StyledContainerColumnLeft>
              </StyledContainerRowStart>
              <Divider />
              <StyledContainerRowBetween
                sx={{
                  gap: isDownSm ? 2 : 4,
                  alignItems: 'flex-start',
                  flexDirection: isDownSm ? 'column' : 'row',
                }}
              >
                <StyledContainerColumnLeft>
                  <ScheduleDialogDescriptionBlock
                    title={t('page.schedule.fillOutManyPopup.timeFrameTitle')}
                    description={t('page.schedule.fillOutManyPopup.timeFrameDescription')}
                  />
                </StyledContainerColumnLeft>
                <StyledContainerColumnRight sx={{ gap: 2, flexDirection: isDownSm ? 'row' : 'column' }}>
                  <DateRangePickerWithPresets
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    column
                  />
                </StyledContainerColumnRight>
              </StyledContainerRowBetween>

              <Divider />

              {reportType === ReportType.Projects && (
                <StyledContainerRowBetween
                  sx={{
                    gap: isDownSm ? 2 : 4,
                    alignItems: 'flex-start',
                    flexDirection: isDownSm ? 'column' : 'row',
                  }}
                >
                  <StyledContainerColumnLeft>
                    <ScheduleDialogDescriptionBlock
                      title={t('page.reports.projectReportType')}
                      description={t('page.reports.projectReportShouldInclude')}
                    />
                  </StyledContainerColumnLeft>
                  <StyledContainerColumnRight>
                    {projectsTypeArray.map(({ type, label }) => (
                      <FormControlLabel
                        key={type}
                        sx={{
                          maxWidth: '100%',
                          color: 'text.main',
                          margin: 0,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        control={
                          <Checkbox
                            checked={selectedType === type}
                            onChange={() => setSelectedType(type)}
                            inputProps={{ 'aria-label': t(label) }}
                          />
                        }
                        label={t(label)}
                        labelPlacement="end"
                      />
                    ))}
                  </StyledContainerColumnRight>
                </StyledContainerRowBetween>
              )}

              {reportType === ReportType.Projects && <Divider />}

              <StyledContainerRowEnd sx={{ gap: 2 }}>
                <Button sx={{ color: 'black', fontWeight: 'bold', px: 5 }} onClick={popupState.close}>
                  {t('components.button.leave')}
                </Button>
                <StyledButtonPrimary
                  sx={{ fontWeight: 'bold', px: 5 }}
                  variant="contained"
                  size="medium"
                  disabled={startDate === null || endDate === null || endDate.isBefore(startDate)}
                  onClick={async () => {
                    try {
                      const stDate = startDate?.format(DateFormat.YearMonthDay)
                      const eDate = endDate?.format(DateFormat.YearMonthDay)
                      let response
                      if (reportType === ReportType.Employee) {
                        const employeeId = selectedData[0].id
                        response = await handleGetEmployeeReport({
                          employeeId: employeeId,
                          startDate: stDate!,
                          endDate: eDate!,
                        })
                      }
                      if (reportType === ReportType.Employees && leaderId) {
                        const employeeIds = selectedData.map(data => data.id)
                        response = await handleGetEmployeesReport({
                          employeeIds: employeeIds,
                          startDate: stDate!,
                          endDate: eDate!,
                          leaderId: leaderId,
                        })
                      }
                      if (reportType === ReportType.Projects && leaderId) {
                        const projectIds = selectedData.map(data => data.id)
                        response = await handleGetProjectReport({
                          projectIds: projectIds,
                          startDate: stDate!,
                          endDate: eDate!,
                          projectReportType: selectedType,
                          leaderId: leaderId,
                          includeCosts: true,
                        })
                      }

                      const blob = new Blob([response?.data], {
                        type: response?.headers['content-type'] as string | undefined,
                      })
                      const contentDisposition = response?.headers['content-disposition']

                      if (contentDisposition) {
                        let fileName = contentDisposition.split('filename=')[1].replace(/^"(.*)"$/, '$1')
                        fileName = decodeURIComponent(fileName)
                        saveAs(blob, fileName)
                      } else {
                        saveAs(blob, 'report')
                      }
                    } catch (error) {
                      if (axios.isAxiosError(error) && error.response) {
                        const contentType = error.response.headers['content-type'] as string | undefined
                        if (contentType && contentType.includes('application/json')) {
                          const reader = new FileReader()
                          reader.onloadend = () => {
                            const jsonData = JSON.parse(reader.result as string)
                            displayNotification({ message: createErrorMessage(jsonData.error, []), type: 'error' })
                          }

                          reader.readAsText(error.response.data)
                        } else {
                          displayNotification({ message: t('page.projects.notificationPopup.error'), type: 'error' })
                        }
                      }
                    }
                    popupState.close()
                  }}
                >
                  {t('components.button.download')}
                </StyledButtonPrimary>
              </StyledContainerRowEnd>
            </StyledContainerColumnLeft>
          </Dialog>
        </React.Fragment>
      )}
    </PopupState>
  )
}

export default GenerateReportPopup
